import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Alert,
  Box,
} from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { PURPLE, PURPLE_DARK } from "./leadConstants";
import {
  deliveryAddressFromQuotation,
  normalizeDeliveryAddress,
  validateDeliveryAddress,
} from "../utils/quotationAddressUtils";
import { formatQuotationLabel } from "../utils/quotationUtils";

export default function QuotationConvertAddressDialog({
  open,
  quotation,
  onClose,
  onConfirm,
  submitting = false,
}) {
  const [form, setForm] = useState(deliveryAddressFromQuotation(quotation));
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && quotation) {
      setForm(deliveryAddressFromQuotation(quotation));
      setError("");
    }
  }, [open, quotation]);

  const updateField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    if (error) setError("");
  };

  const handleSubmit = () => {
    const normalized = normalizeDeliveryAddress(form);
    const validationError = validateDeliveryAddress(normalized);
    if (validationError) {
      setError(validationError);
      return;
    }
    onConfirm?.(normalized);
  };

  if (!quotation) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LocalShippingOutlinedIcon sx={{ color: PURPLE }} />
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Delivery address
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Required to create order for
            {" "}
            {formatQuotationLabel(quotation.quotation_number)}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter the customer delivery details. These will be used to create the order in your connected store.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Customer name"
              value={form.name}
              onChange={updateField("name")}
              fullWidth
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone"
              value={form.phone}
              onChange={updateField("phone")}
              fullWidth
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={updateField("email")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address line 1"
              value={form.address1}
              onChange={updateField("address1")}
              fullWidth
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address line 2"
              value={form.address2}
              onChange={updateField("address2")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="City"
              value={form.city}
              onChange={updateField("city")}
              fullWidth
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="State"
              value={form.state}
              onChange={updateField("state")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="PIN code"
              value={form.pincode}
              onChange={updateField("pincode")}
              fullWidth
              size="small"
              required
              inputProps={{ maxLength: 6 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Country"
              value={form.country}
              onChange={updateField("country")}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_DARK } }}
        >
          {submitting ? "Converting…" : "Convert to order"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
